/**
 * PursuitPageShell — canonical layout wrapper for every pursuit screen.
 *
 * Replaces the inconsistent `p-6 max-w-5xl mx-auto` (Capability Statement,
 * Agency Questions) and assorted ad-hoc wrappers with a single shape that
 * matches the legacy pursuit pages (ComplianceTrackerLive, GoNoGo, Gap
 * Analysis, Submission Compliance). One source of truth for:
 *   - min-h-screen + slate-50 page background
 *   - white sticky-style header band (px-8 py-4) with optional breadcrumb,
 *     title, subtitle, and right-aligned action cluster
 *   - PursuitLifecycleStepper rendered immediately below the header,
 *     aligned to the same px-8 gutter
 *   - Body padding p-8 with built-in `space-y-6` vertical rhythm
 *   - Optional <PursuitNextStepFooter /> stitched in at the bottom
 *
 * v3.18.19 — Layout-consistency pass. User feedback: "Submission Day tab,
 * Capability Statement, and Questions for Agency look different from the
 * other pursuit screens. I want consistency everywhere."
 */
import React from 'react';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import PursuitLifecycleStepper from './PursuitLifecycleStepper';
import PursuitNextStepFooter from './PursuitNextStepFooter';
// Note: PursuitLifecycleStepper auto-suppresses itself when the layout top bar
// is active (topBarActive === true in PursuitLayoutContext), so PursuitPageShell
// doesn't need special logic here — just rendering it is enough.

export default function PursuitPageShell({
  // Header
  breadcrumb,        // { label, onClick } — optional Back button (e.g. Back to Pipeline)
  title,             // string or node
  titleIcon: TitleIcon = null, // optional lucide icon component
  subtitle,          // optional string (usually opportunity_name)
  eyebrow,           // optional node rendered above title (e.g. agency_name + phase badge)
  actions,           // node — right-aligned button cluster
  // Stepper + Footer
  showStepper = true,
  nextStepFor,       // string — currentStep prop for PursuitNextStepFooter; null/undefined to hide footer
  // Layout
  bodyClassName = '',
  testId,
  children,
}) {
  return (
    <div
      className="min-h-screen bg-slate-50"
      data-testid={testId || 'pursuit-page-shell'}
    >
      {/* Header band — px-8 py-4, content centered at max-w-7xl so it
          aligns flush with the body's centered column on wide monitors */}
      {(breadcrumb || title || actions) && (
        <div className="bg-white border-b border-slate-200 px-8 py-4">
          <div className="max-w-7xl mx-auto flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              {breadcrumb && (
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2"
                    onClick={breadcrumb.onClick}
                    data-testid="pursuit-shell-breadcrumb-btn"
                  >
                    <ArrowLeft className="w-3 h-3 mr-1" />
                    {breadcrumb.label}
                  </Button>
                  {title && <ChevronRight className="w-4 h-4" />}
                  {title && <span className="text-slate-700">{title}</span>}
                </div>
              )}
              {eyebrow && <div className="mb-1">{eyebrow}</div>}
              {title && (
                <h1
                  className="text-2xl font-semibold text-slate-900 flex items-center gap-2"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {TitleIcon && <TitleIcon className="w-6 h-6 text-violet-700" />}
                  {title}
                </h1>
              )}
              {subtitle && (
                <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
              )}
            </div>
            {actions && (
              <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                {actions}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lifecycle stepper — same px-8 gutter as the header, centered too */}
      {showStepper && (
        <div className="px-8">
          <div className="max-w-7xl mx-auto">
            <PursuitLifecycleStepper />
          </div>
        </div>
      )}

      {/* Body — p-8 + space-y-6 vertical rhythm. v3.18.20 — content centered
          at max-w-7xl (1280px) so wide monitors don't stretch forms across
          the full viewport. Matches Opportunity Intake and Strategy. */}
      <div className="p-8">
        <div className={`max-w-7xl mx-auto space-y-6 ${bodyClassName}`}>
          {children}
        </div>
      </div>

      {/* Optional next-step footer */}
      {nextStepFor && (
        <div className="px-8 pb-8">
          <div className="max-w-7xl mx-auto">
            <PursuitNextStepFooter currentStep={nextStepFor} />
          </div>
        </div>
      )}
    </div>
  );
}
